/**
 * SF Symbol names → lucide glyphs for the `image` element kind.
 *
 * The Swift side sends `Image(systemName:)` names unchanged; SF Symbols are
 * not redistributable, so the renderer draws the closest glyph from lucide
 * (ISC). Icons are inline SVG at `1em`, stroked with `currentColor`, so they
 * follow the surrounding font size and foreground color like SF Symbols do.
 *
 * `.fill` variants: lucide only ships outlines, so a filled variant is drawn
 * by filling the glyph's outer shape with `currentColor` and stroking the
 * inner marks with the background color (`knockout`), or by filling every
 * path (`solid`) when the glyph is a single silhouette.
 */

import {
  Accessibility,
  Activity,
  Airplay,
  ALargeSmall,
  AlarmClock,
  Archive,
  ArrowBigDown,
  ArrowBigLeft,
  ArrowBigRight,
  ArrowBigUp,
  ArrowDown,
  ArrowDownLeft,
  ArrowDownRight,
  ArrowDownToLine,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpFromLine,
  ArrowUpLeft,
  ArrowUpRight,
  AtSign,
  AudioWaveform,
  Award,
  BadgeCheck,
  BadgeQuestionMark,
  Ban,
  Bandage,
  Banknote,
  Barcode,
  Battery,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  BatteryWarning,
  BedDouble,
  Bell,
  BellDot,
  BellOff,
  Bike,
  Bird,
  Bitcoin,
  Bluetooth,
  Bold,
  CaseSensitive,
  Book,
  Bookmark,
  BookOpen,
  Brain,
  Briefcase,
  BriefcaseMedical,
  Bug,
  Building,
  Bus,
  Cake,
  Calendar,
  CalendarClock,
  CalendarPlus,
  CalendarX,
  Camera,
  Car,
  Carrot,
  Cat,
  ChartBar,
  ChartColumn,
  ChartLine,
  ChartPie,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUp,
  ChevronsUpDown,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleArrowDown,
  CircleArrowLeft,
  CircleArrowRight,
  CircleArrowUp,
  CircleCheck,
  CircleDollarSign,
  CircleDot,
  CircleEllipsis,
  CircleMinus,
  CirclePause,
  CirclePlay,
  CirclePlus,
  CircleQuestionMark,
  CircleStop,
  CircleUser,
  CircleX,
  Clapperboard,
  Clipboard,
  ClipboardList,
  Clock,
  ClockCheck,
  Cloud,
  CloudDownload,
  CloudDrizzle,
  CloudFog,
  CloudHail,
  CloudLightning,
  CloudMoon,
  CloudRain,
  CloudRainWind,
  CloudSnow,
  CloudSun,
  CloudUpload,
  Code,
  Coffee,
  Cog,
  Columns2,
  Command,
  Compass,
  Contact,
  Contrast,
  Copy,
  CornerDownLeft,
  CreditCard,
  Crop,
  Cross,
  Crown,
  Database,
  Delete,
  Eraser,
  Diamond,
  Dices,
  Dog,
  DollarSign,
  Download,
  Droplet,
  Droplets,
  Dumbbell,
  Earth,
  Ellipsis,
  Euro,
  Eye,
  EyeOff,
  FaceSlightlySmiling,
  File,
  FilePlus,
  FileText,
  Film,
  Fingerprint,
  Fish,
  Flag,
  FlagOff,
  Flame,
  Folder,
  FolderMinus,
  FolderPlus,
  Forward,
  Fuel,
  Funnel,
  GalleryVerticalEnd,
  Gamepad2,
  Gauge,
  Gift,
  GitBranch,
  GitMerge,
  Glasses,
  Globe,
  GraduationCap,
  Grid3x3,
  Hammer,
  Hand,
  HardDrive,
  Hash,
  Headphones,
  Heart,
  HeartOff,
  Hexagon,
  Highlighter,
  History,
  Hourglass,
  House,
  IdCard,
  Image,
  Images,
  Inbox,
  Infinity,
  Info,
  Italic,
  JapaneseYen,
  Key,
  Keyboard,
  Landmark,
  Laptop,
  Lasso,
  Layers,
  Layers3,
  LayoutGrid,
  LayoutList,
  Leaf,
  Library,
  Lightbulb,
  LineSquiggle,
  Link,
  Link2,
  List,
  ListChecks,
  ListFilter,
  ListIndentDecrease,
  ListIndentIncrease,
  ListMusic,
  ListOrdered,
  LoaderCircle,
  LocateFixed,
  Lock,
  LockOpen,
  LogIn,
  LogOut,
  Mail,
  MailOpen,
  MailWarning,
  Map,
  MapPin,
  MapPinOff,
  Maximize2,
  Medal,
  Menu,
  MessageCircle,
  MessageSquare,
  MessageSquareMore,
  MessageSquarePlus,
  MessageSquareQuote,
  MessageSquareText,
  MessageSquareWarning,
  MessagesSquare,
  Mic,
  MicOff,
  MicVocal,
  Minimize2,
  Minus,
  Monitor,
  MonitorPlay,
  Moon,
  Mountain,
  MousePointer2,
  Music,
  Navigation2,
  Network,
  Newspaper,
  Octagon,
  OctagonAlert,
  OctagonX,
  Option,
  Package,
  Paintbrush,
  Palette,
  PanelLeft,
  PanelRight,
  Paperclip,
  Pause,
  PawPrint,
  Pencil,
  PencilOff,
  PenTool,
  Percent,
  PersonStanding,
  Phone,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
  Pill,
  Pin,
  Pipette,
  Plane,
  Play,
  Plus,
  Pointer,
  PoundSterling,
  Power,
  Printer,
  Puzzle,
  QrCode,
  Rabbit,
  Rainbow,
  Radio,
  RectangleHorizontal,
  RectangleVertical,
  Redo2,
  RefreshCw,
  Repeat,
  Reply,
  ReplyAll,
  RotateCcw,
  RotateCw,
  Rows2,
  Ruler,
  Sailboat,
  Scan,
  ScanBarcode,
  ScanFace,
  ScanQrCode,
  School,
  Scissors,
  Search,
  Send,
  Server,
  Settings,
  Shapes,
  Share,
  Share2,
  Shield,
  ShieldCheck,
  Ship,
  ShoppingBag,
  ShoppingBasket,
  ShoppingCart,
  Shuffle,
  Sigma,
  Signal,
  Signpost,
  SkipBack,
  SkipForward,
  SlidersHorizontal,
  Smartphone,
  Snowflake,
  Sparkle,
  Sparkles,
  Speaker,
  Square,
  SquareArrowDown,
  SquareArrowLeft,
  SquareArrowOutUpRight,
  SquareArrowRight,
  SquareArrowUp,
  SquareCheck,
  SquareMinus,
  SquareParking,
  SquarePen,
  SquareDashed,
  SquarePlay,
  SquarePlus,
  SquareUser,
  SquareX,
  Star,
  StarHalf,
  StarOff,
  Stethoscope,
  StickyNote,
  Store,
  Strikethrough,
  Sun,
  SunDim,
  Sunrise,
  Sunset,
  SwitchCamera,
  Tag,
  Target,
  Tent,
  Terminal,
  Cpu,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignJustify,
  TextAlignStart,
  TextCursorInput,
  Thermometer,
  ThumbsDown,
  ThumbsUp,
  Ticket,
  Timer,
  Tornado,
  TrainFront,
  TramFront,
  Trash,
  Trash2,
  TreeDeciduous,
  TrendingDown,
  TrendingUp,
  Triangle,
  TriangleAlert,
  Trophy,
  Truck,
  Turtle,
  Tv,
  Type,
  Umbrella,
  Underline,
  Undo2,
  Upload,
  User,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
  UserX,
  Utensils,
  Video,
  VideoOff,
  Volume,
  Volume1,
  Volume2,
  VolumeX,
  Wallet,
  WandSparkles,
  Watch,
  Wifi,
  WifiOff,
  Wind,
  Wine,
  Wrench,
  X,
  Zap,
  ZapOff,
  ZoomIn,
  ZoomOut,
  createElement,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  Box,
  Braces,
  CircleDashed,
  Club,
  Columns3,
  CornerDownRight,
  CornerUpLeft,
  CornerUpRight,
  Dice1,
  Dice2,
  Dice3,
  Dice4,
  Dice5,
  Dice6,
  Diff,
  Divide,
  Equal,
  FastForward,
  FlipHorizontal2,
  FlipVertical2,
  Layers2,
  MemoryStick,
  Move3d,
  PinOff,
  Radical,
  Rewind,
  Rotate3d,
  RotateCcwSquare,
  RotateCwSquare,
  Scale3d,
  Signature,
  SlidersVertical,
  Spade,
  Table,
  Tablet,
  TextCursor,
  Waves,
  type IconNode,
} from 'lucide';

/** How a glyph is painted; see the file comment. */
export type GlyphFill = 'outline' | 'knockout' | 'solid';

export interface SymbolGlyph {
  icon: IconNode;
  fill: GlyphFill;
}

/**
 * The activity indicator (an indeterminate `ProgressView`), as iOS draws it:
 * eight rounded spokes filling the glyph, the leading one (at the top) at full
 * strength and the three behind it fading out to the rest's 0.31. styles.css
 * steps that ramp around the spokes clockwise (the glyph itself stays still),
 * so the fade trails the leading spoke.
 */
const ACTIVITY_INDICATOR: IconNode = [
  ['line', { x1: '12', y1: '6.21', x2: '12', y2: '1.52', 'stroke-width': '3.05', 'stroke-opacity': '1' }],
  ['line', { x1: '16.09', y1: '7.91', x2: '19.41', y2: '4.59', 'stroke-width': '3.05', 'stroke-opacity': '0.31' }],
  ['line', { x1: '17.79', y1: '12', x2: '22.48', y2: '12', 'stroke-width': '3.05', 'stroke-opacity': '0.31' }],
  ['line', { x1: '16.09', y1: '16.09', x2: '19.41', y2: '19.41', 'stroke-width': '3.05', 'stroke-opacity': '0.31' }],
  ['line', { x1: '12', y1: '17.79', x2: '12', y2: '22.48', 'stroke-width': '3.05', 'stroke-opacity': '0.31' }],
  ['line', { x1: '7.91', y1: '16.09', x2: '4.59', y2: '19.41', 'stroke-width': '3.05', 'stroke-opacity': '0.49' }],
  ['line', { x1: '6.21', y1: '12', x2: '1.52', y2: '12', 'stroke-width': '3.05', 'stroke-opacity': '0.66' }],
  ['line', { x1: '7.91', y1: '7.91', x2: '4.59', y2: '4.59', 'stroke-width': '3.05', 'stroke-opacity': '0.83' }],
];

/** `books.vertical.fill`: three upright books and a leaning one, as solid shapes. */
const BOOKS_VERTICAL_FILL: IconNode = [
  ['rect', { x: '1.5', y: '3', width: '5', height: '18', rx: '1.2', stroke: 'none' }],
  ['rect', { x: '7.5', y: '3', width: '5', height: '18', rx: '1.2', stroke: 'none' }],
  ['rect', { x: '13.5', y: '5', width: '4', height: '16', rx: '1.2', stroke: 'none' }],
  ['rect', { x: '17.8', y: '4.5', width: '4', height: '16', rx: '1.2', stroke: 'none', transform: 'rotate(-14 19.8 12.5)' }],
];

/** `apple.logo`: the Apple logo (the simple-icons path, CC0), as one solid shape, for a Sign in with Apple button. */
const APPLE_LOGO: IconNode = [
  [
    'path',
    {
      d: 'M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701',
      stroke: 'none',
    },
  ],
];

/** Marks a `knockout` glyph's inner shape that is filled, not only stroked (`enclosed`). */
const SOLID_MARK = 'data-sb-knockout-solid';

/** Color used for inner marks of `knockout` glyphs; styles.css sets the variable. */
const KNOCKOUT_COLOR = 'var(--sb-icon-knockout, var(--sb-color-system-background, #fff))';

/** `square.on.circle.fill`: a solid square over a solid circle, a gap between them. */
const SQUARE_ON_CIRCLE_FILL: IconNode = [
  ['circle', { cx: '9.5', cy: '9.5', r: '7.5' }],
  ['rect', { x: '9', y: '9', width: '13', height: '13', rx: '3', stroke: KNOCKOUT_COLOR, 'stroke-width': '2.4' }],
];

/**
 * A numeral in a circle or rounded square (`1.circle`, `1.square.fill`):
 * lucide has none, so the numeral is drawn in its stroke style, the
 * enclosure the size `enclosed` gives other glyphs'.
 */
const NUMERALS: Readonly<Record<string, IconNode>> = {
  // each about 5 wide and 9 high, centered on the glyph's middle
  '0': [['rect', { x: '9.5', y: '7.5', width: '5', height: '9', rx: '2.5' }]],
  // a stem with a flag at its top, as lucide's `Heading1` draws its 1
  '1': [['path', { d: 'M9.5 9.5 12.5 7.5v9' }]],
  '2': [['path', { d: 'M9.5 9.75a2.5 2.5 0 0 1 5 0c0 1.4-.9 2.4-2 3.5L9.5 16.5h5' }]],
  '3': [['path', { d: 'M9.5 7.5h5L12 11a2.75 2.75 0 1 1-1.94 4.69' }]],
  '4': [['path', { d: 'M13.5 16.5v-9L9 13.5h6' }]],
  '5': [['path', { d: 'M14.5 7.5H10l-.5 4.25c.7-.5 1.5-.75 2.5-.75a2.75 2.75 0 0 1 0 5.5c-1 0-2-.5-2.5-1.25' }]],
  '6': [
    ['path', { d: 'M14 7.5c-2.5 1-4.5 3.5-4.5 6.5' }],
    ['circle', { cx: '12', cy: '14', r: '2.5' }],
  ],
  '7': [['path', { d: 'M9.5 7.5h5l-3.5 9' }]],
  '8': [
    ['circle', { cx: '12', cy: '9.5', r: '2' }],
    ['circle', { cx: '12', cy: '14', r: '2.5' }],
  ],
  '9': [
    ['circle', { cx: '12', cy: '10', r: '2.5' }],
    ['path', { d: 'M14.5 10c0 3-2 5.5-4.5 6.5' }],
  ],
};

function numeral(digit: string, enclosure: 'circle' | 'square'): IconNode {
  const outer: IconNode[number] =
    enclosure === 'circle' ? ['circle', { cx: '12', cy: '12', r: '10' }] : ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '4' }];
  return [outer, ...NUMERALS[digit]!];
}

/** `0.circle` … `9.square.fill`: every numeral, in a circle or a square, outlined or filled. */
const NUMERAL_SYMBOLS: Record<string, SymbolGlyph> = Object.fromEntries(
  Object.keys(NUMERALS).flatMap((digit) =>
    (['circle', 'square'] as const).flatMap((enclosure) => [
      [`${digit}.${enclosure}`, { icon: numeral(digit, enclosure), fill: 'outline' }],
      [`${digit}.${enclosure}.fill`, { icon: numeral(digit, enclosure), fill: 'knockout' }],
    ]),
  ),
);

/** `info`: the i on its own (lucide draws it only in a circle), a foot under its stem. */
const INFO: IconNode = [
  ['path', { d: 'M10 10h2v10' }],
  ['path', { d: 'M9.5 20h5' }],
  ['path', { d: 'M12 5h.01' }],
];

/** `exclamationmark`: the mark on its own. */
const EXCLAMATIONMARK: IconNode = [
  ['path', { d: 'M12 4v11' }],
  ['path', { d: 'M12 20h.01' }],
];

/** `questionmark`: lucide's `CircleQuestionMark` mark on its own, scaled up to fill the glyph. */
const QUESTIONMARK: IconNode = [
  ['path', { d: 'M6.91 6.53a5.25 5.25 0 0 1 10.2 1.75c0 3.5-5.25 5.25-5.25 5.25' }],
  ['path', { d: 'M12 20.5h.01' }],
];

/**
 * `tray.fill`: the tray's silhouette (lucide's `Inbox`) filled, split along
 * its opening's edge, the line with the notch, by a gap: the well above,
 * the front below. A real gap, not a stroke in the background color, so
 * whatever is behind the glyph (a selected tab's capsule) shows through.
 * The pieces' edges along it sit 1.75 from that line, so with their 2 wide
 * stroke they leave 1.5 between them, the notch's slants offset square to
 * their direction.
 */
const TRAY_FILL: IconNode = [
  ['path', { d: 'M2.88 10.25 5.45 5.11A2 2 0 0 1 7.24 4h9.52a2 2 0 0 1 1.79 1.11l2.57 5.14h-6.06l-2 3h-2.12l-2-3z' }],
  ['path', { d: 'M2 13.75h5.06l2 3h5.88l2-3H22V18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z' }],
];

/** `circle.grid.2x2`: four dots in a square, as lucide draws none. */
const CIRCLE_GRID_2X2: IconNode = [
  ['circle', { cx: '7', cy: '7', r: '3.5' }],
  ['circle', { cx: '17', cy: '7', r: '3.5' }],
  ['circle', { cx: '7', cy: '17', r: '3.5' }],
  ['circle', { cx: '17', cy: '17', r: '3.5' }],
];

/** `trash.slash`: lucide's trash crossed out, as its `…Off` icons are. */
const TRASH_SLASH: IconNode = [...Trash, ['path', { d: 'm2 2 20 20' }]];

const outline = (icon: IconNode): SymbolGlyph => ({ icon, fill: 'outline' });
const knockout = (icon: IconNode): SymbolGlyph => ({ icon, fill: 'knockout' });
const solid = (icon: IconNode): SymbolGlyph => ({ icon, fill: 'solid' });

/** SF Symbol name → glyph. Keys are the exact `systemName` strings. */
export const SF_SYMBOLS: Readonly<Record<string, SymbolGlyph>> = {
  // Marks
  plus: outline(Plus),
  minus: outline(Minus),
  checkmark: outline(Check),
  'checkmark.circle': outline(CircleCheck),
  'checkmark.circle.fill': knockout(CircleCheck),
  'checkmark.square': outline(SquareCheck),
  'checkmark.square.fill': knockout(SquareCheck),
  checklist: outline(ListChecks),
  xmark: outline(X),
  'xmark.circle': outline(CircleX),
  'xmark.circle.fill': knockout(CircleX),
  'plus.circle': outline(CirclePlus),
  'plus.circle.fill': knockout(CirclePlus),
  'minus.circle': outline(CircleMinus),
  'minus.circle.fill': knockout(CircleMinus),
  'info.circle': outline(Info),
  'info.circle.fill': knockout(Info),
  questionmark: outline(QUESTIONMARK),
  'questionmark.circle': outline(CircleQuestionMark),
  'questionmark.circle.fill': knockout(CircleQuestionMark),
  'exclamationmark.circle': outline(CircleAlert),
  'exclamationmark.circle.fill': knockout(CircleAlert),
  'exclamationmark.triangle': outline(TriangleAlert),
  'exclamationmark.triangle.fill': knockout(TriangleAlert),
  ellipsis: outline(Ellipsis),
  'ellipsis.circle': outline(CircleEllipsis),
  'ellipsis.circle.fill': knockout(CircleEllipsis),

  // Shapes
  circle: outline(Circle),
  'circle.fill': solid(Circle),
  'circle.inset.filled': outline(CircleDot),
  'largecircle.fill.circle': outline(CircleDot),
  ...NUMERAL_SYMBOLS,
  // media, keys, math, dice and cards, layout and 3D tools, the bare info and exclamation marks
  backward: outline(Rewind),
  'backward.fill': solid(Rewind),
  forward: outline(FastForward),
  'forward.fill': solid(FastForward),
  goforward: outline(RotateCw),
  gobackward: outline(RotateCcw),
  // The skips' number is not drawn: the arrow alone.
  'gobackward.10': outline(RotateCcw),
  'gobackward.15': outline(RotateCcw),
  'gobackward.30': outline(RotateCcw),
  'goforward.10': outline(RotateCw),
  'goforward.15': outline(RotateCw),
  'goforward.30': outline(RotateCw),
  'record.circle': outline(CircleDot),
  cube: outline(Box),
  'cube.fill': outline(Box),
  shift: outline(ArrowBigUp),
  'shift.fill': solid(ArrowBigUp),
  control: outline(ChevronUp),
  curlybraces: outline(Braces),
  equal: outline(Equal),
  divide: outline(Divide),
  multiply: outline(X),
  plusminus: outline(Diff),
  'x.squareroot': outline(Radical),
  'die.face.1': outline(Dice1),
  'die.face.2': outline(Dice2),
  'die.face.3': outline(Dice3),
  'die.face.4': outline(Dice4),
  'die.face.5': outline(Dice5),
  'die.face.6': outline(Dice6),
  'die.face.1.fill': knockout(Dice1),
  'die.face.2.fill': knockout(Dice2),
  'die.face.3.fill': knockout(Dice3),
  'die.face.4.fill': knockout(Dice4),
  'die.face.5.fill': knockout(Dice5),
  'die.face.6.fill': knockout(Dice6),
  display: outline(Monitor),
  ipad: outline(Tablet),
  internaldrive: outline(HardDrive),
  memorychip: outline(MemoryStick),
  // lucide names its flips by the axis line: its FlipVertical2 mirrors left to right
  'flip.horizontal': outline(FlipVertical2),
  'flip.vertical': outline(FlipHorizontal2),
  'rotate.left': outline(RotateCcwSquare),
  'rotate.right': outline(RotateCwSquare),
  'align.horizontal.left': outline(AlignStartVertical),
  'align.horizontal.center': outline(AlignCenterVertical),
  'align.vertical.top': outline(AlignStartHorizontal),
  'align.vertical.center': outline(AlignCenterHorizontal),
  'distribute.horizontal.center': outline(AlignHorizontalDistributeCenter),
  'move.3d': outline(Move3d),
  'rotate.3d': outline(Rotate3d),
  'scale.3d': outline(Scale3d),
  'square.2.layers.3d': outline(Layers2),
  'square.3.layers.3d': outline(Layers3),
  'square.3.stack.3d': outline(Layers),
  'hand.point.up.left': outline(Pointer),
  'pin.slash': outline(PinOff),
  'pin.slash.fill': outline(PinOff),
  'square.dotted': outline(SquareDashed),
  'rectangle.dashed': outline(SquareDashed),
  'circle.dashed': outline(CircleDashed),
  'rectangle.split.3x1': outline(Columns3),
  tablecells: outline(Table),
  'tablecells.fill': outline(Table),
  signature: outline(Signature),
  'sidebar.leading': outline(PanelLeft),
  'sidebar.trailing': outline(PanelRight),
  'slider.vertical.3': outline(SlidersVertical),
  'suit.heart': outline(Heart),
  'suit.heart.fill': solid(Heart),
  'suit.club': outline(Club),
  'suit.club.fill': solid(Club),
  'suit.spade': outline(Spade),
  'suit.spade.fill': solid(Spade),
  'suit.diamond': outline(Diamond),
  'suit.diamond.fill': solid(Diamond),
  target: outline(Target),
  'water.waves': outline(Waves),
  'text.cursor': outline(TextCursor),
  'arrow.turn.down.left': outline(CornerDownLeft),
  'arrow.turn.down.right': outline(CornerDownRight),
  'arrow.turn.up.left': outline(CornerUpLeft),
  'arrow.turn.up.right': outline(CornerUpRight),
  'chevron.compact.down': outline(ChevronDown),
  'chevron.compact.up': outline(ChevronUp),
  'chevron.compact.left': outline(ChevronLeft),
  'chevron.compact.right': outline(ChevronRight),
  info: outline(INFO),
  exclamationmark: outline(EXCLAMATIONMARK),
  square: outline(Square),
  'square.fill': solid(Square),
  star: outline(Star),
  'star.fill': solid(Star),
  heart: outline(Heart),
  'heart.fill': solid(Heart),
  bolt: outline(Zap),
  'bolt.fill': solid(Zap),
  sparkles: outline(Sparkles),
  // Apple Intelligence's mark: the sparkles stand in for it.
  'apple.intelligence': outline(Sparkles),
  rainbow: outline(Rainbow),

  // Chevrons and arrows
  'chevron.left': outline(ChevronLeft),
  'chevron.right': outline(ChevronRight),
  'chevron.up': outline(ChevronUp),
  'chevron.down': outline(ChevronDown),
  'chevron.left.2': outline(ChevronsLeft),
  'chevron.right.2': outline(ChevronsRight),
  'chevron.up.2': outline(ChevronsUp),
  'chevron.up.chevron.down': outline(ChevronsUpDown),
  'chevron.down.2': outline(ChevronsDown),
  'chevron.backward': outline(ChevronLeft),
  'chevron.forward': outline(ChevronRight),
  'arrow.left': outline(ArrowLeft),
  'arrow.right': outline(ArrowRight),
  'arrow.up': outline(ArrowUp),
  'arrow.down': outline(ArrowDown),
  'arrow.backward': outline(ArrowLeft),
  'arrow.forward': outline(ArrowRight),
  'arrow.up.right': outline(ArrowUpRight),
  'arrow.left.arrow.right': outline(ArrowLeftRight),
  'arrow.up.arrow.down': outline(ArrowUpDown),
  'arrow.left.circle': outline(CircleArrowLeft),
  'arrow.right.circle': outline(CircleArrowRight),
  'arrow.up.circle': outline(CircleArrowUp),
  'arrow.up.circle.fill': knockout(CircleArrowUp),
  'arrow.down.circle': outline(CircleArrowDown),
  'arrow.down.to.line': outline(ArrowDownToLine),
  'arrow.up.to.line': outline(ArrowUpFromLine),
  'arrow.clockwise': outline(RotateCw),
  'arrow.counterclockwise': outline(RotateCcw),
  'arrow.triangle.2.circlepath': outline(RefreshCw),
  'arrow.2.squarepath': outline(RefreshCw),
  repeat: outline(Repeat),
  shuffle: outline(Shuffle),
  'square.and.arrow.up': outline(Share),
  'square.and.arrow.down': outline(Download),
  'arrow.down.doc': outline(Download),
  'arrow.up.doc': outline(Upload),
  'rectangle.portrait.and.arrow.right': outline(LogOut),
  'rectangle.portrait.and.arrow.forward': outline(LogOut),
  'arrow.right.to.line': outline(LogIn),

  // Editing
  trash: outline(Trash),
  'trash.fill': outline(Trash),
  pencil: outline(Pencil),
  // The editing tools of a drawing app: the pointer (macOS's arrow), a marquee, the eraser.
  cursorarrow: outline(MousePointer2),
  'square.dashed': outline(SquareDashed),
  eraser: outline(Eraser),
  'square.and.pencil': outline(SquarePen),
  'doc.on.doc': outline(Copy),
  'doc.on.clipboard': outline(Clipboard),
  paperclip: outline(Paperclip),
  pin: outline(Pin),
  'pin.fill': solid(Pin),
  bookmark: outline(Bookmark),
  'bookmark.fill': solid(Bookmark),
  flag: outline(Flag),
  'flag.fill': solid(Flag),
  'flag.slash': outline(FlagOff),
  'flag.slash.fill': outline(FlagOff),
  tag: outline(Tag),
  'tag.fill': solid(Tag),
  textformat: outline(CaseSensitive),
  'line.3.horizontal': outline(Menu),
  // Three lines, each shorter than the one above.
  'line.3.horizontal.decrease': outline(ListFilter),
  'line.3.horizontal.decrease.circle': outline(Funnel),
  'slider.horizontal.3': outline(SlidersHorizontal),
  'list.bullet': outline(List),
  'list.number': outline(ListOrdered),
  'chart.bar': outline(ChartBar),
  'chart.xyaxis.line': outline(ChartLine),
  'chart.pie': outline(ChartPie),
  magnifyingglass: outline(Search),
  'plus.magnifyingglass': outline(ZoomIn),
  'minus.magnifyingglass': outline(ZoomOut),

  // People and places
  person: outline(User),
  'person.fill': solid(User),
  'person.2': outline(Users),
  'person.circle': outline(CircleUser),
  'person.circle.fill': knockout(CircleUser),
  'person.crop.circle': outline(CircleUser),
  'person.crop.circle.fill': knockout(CircleUser),
  'figure.walk': outline(Accessibility),
  accessibility: outline(Accessibility),
  'circle.lefthalf.filled': outline(Contrast),
  'face.smiling': outline(FaceSlightlySmiling),
  'hand.thumbsup': outline(ThumbsUp),
  'hand.thumbsup.fill': solid(ThumbsUp),
  'hand.raised': outline(Hand),
  house: outline(House),
  'house.fill': solid(House),
  building: outline(Building),
  'building.2': outline(Building),
  graduationcap: outline(GraduationCap),
  briefcase: outline(Briefcase),
  'briefcase.fill': solid(Briefcase),
  map: outline(Map),
  location: outline(MapPin),
  'location.fill': knockout(MapPin),
  'mappin': outline(MapPin),
  'mappin.and.ellipse': outline(MapPin),
  globe: outline(Globe),
  safari: outline(Compass),
  scope: outline(Target),

  // Communication
  bell: outline(Bell),
  'bell.fill': solid(Bell),
  envelope: outline(Mail),
  'envelope.fill': outline(Mail),
  'envelope.open': outline(Inbox),
  tray: outline(Inbox),
  phone: outline(Phone),
  'phone.fill': solid(Phone),
  message: outline(MessageCircle),
  'message.fill': solid(MessageCircle),
  bubble: outline(MessageSquare),
  'bubble.left': outline(MessageSquare),
  at: outline(AtSign),
  paperplane: outline(Send),
  'paperplane.fill': outline(Send),
  link: outline(Link),
  'link.circle': outline(Link),

  // Time
  calendar: outline(Calendar),
  clock: outline(Clock),
  'clock.fill': knockout(Clock),
  alarm: outline(AlarmClock),
  'applewatch': outline(Watch),
  stopwatch: outline(Timer),
  timer: outline(Timer),
  hourglass: outline(Hourglass),

  // Media and devices
  camera: outline(Camera),
  'camera.fill': outline(Camera),
  photo: outline(Image),
  'photo.fill': knockout(Image),
  'photo.on.rectangle': outline(Image),
  play: outline(Play),
  'play.fill': solid(Play),
  'play.circle': outline(CirclePlay),
  'play.circle.fill': knockout(CirclePlay),
  'pause.circle': outline(CirclePause),
  'pause.circle.fill': knockout(CirclePause),
  'stop.circle': outline(CircleStop),
  'stop.circle.fill': knockout(CircleStop),
  pause: outline(Pause),
  'pause.fill': solid(Pause),
  stop: outline(Square),
  'stop.fill': solid(Square),
  'backward.end': outline(SkipBack),
  'forward.end': outline(SkipForward),
  'backward.end.fill': solid(SkipBack),
  'forward.end.fill': solid(SkipForward),
  'speaker': outline(Volume),
  'speaker.wave.1': outline(Volume1),
  'speaker.wave.2': outline(Volume2),
  'speaker.wave.3': outline(Volume2),
  'speaker.slash': outline(VolumeX),
  'hifispeaker': outline(Speaker),
  headphones: outline(Headphones),
  mic: outline(Mic),
  'mic.fill': outline(Mic),
  'mic.slash': outline(MicOff),
  'music.note': outline(Music),
  tv: outline(Tv),
  desktopcomputer: outline(Monitor),
  laptopcomputer: outline(Laptop),
  iphone: outline(Smartphone),
  keyboard: outline(Keyboard),
  printer: outline(Printer),
  'qrcode': outline(QrCode),
  gamecontroller: outline(Gamepad2),
  'gamecontroller.fill': outline(Gamepad2),
  wifi: outline(Wifi),
  'antenna.radiowaves.left.and.right': outline(Wifi),
  bluetooth: outline(Bluetooth),
  'battery.100': outline(Battery),
  power: outline(Power),
  cpu: outline(Cpu),
  'cpu.fill': knockout(Cpu),
  'chevron.left.forwardslash.chevron.right': outline(Code),
  terminal: outline(Terminal),
  'ant': outline(Bug),
  'ladybug': outline(Bug),
  wrench: outline(Wrench),
  'wrench.and.screwdriver': outline(Wrench),
  'wrench.fill': outline(Wrench),

  // Documents
  folder: outline(Folder),
  'folder.fill': solid(Folder),
  doc: outline(File),
  'doc.fill': outline(File),
  'doc.text': outline(FileText),
  'doc.text.fill': outline(FileText),
  archivebox: outline(Archive),
  'shippingbox': outline(Package),
  'shippingbox.fill': knockout(Package),

  // Settings and security
  gear: outline(Settings),
  gearshape: outline(Settings),
  'gearshape.fill': outline(Settings),
  'gearshape.2': outline(Cog),
  lock: outline(Lock),
  'lock.fill': solid(Lock),
  'lock.open': outline(LockOpen),
  'lock.open.fill': solid(LockOpen),
  key: outline(Key),
  'key.fill': solid(Key),
  shield: outline(Shield),
  'shield.fill': solid(Shield),
  'checkmark.shield': outline(ShieldCheck),
  'checkmark.shield.fill': knockout(ShieldCheck),
  eye: outline(Eye),
  'eye.fill': knockout(Eye),
  'eye.slash': outline(EyeOff),
  'eye.slash.fill': outline(EyeOff),

  // Commerce
  cart: outline(ShoppingCart),
  'cart.fill': outline(ShoppingCart),
  bag: outline(ShoppingBag),
  'bag.fill': outline(ShoppingBag),
  creditcard: outline(CreditCard),
  'creditcard.fill': knockout(CreditCard),
  wallet: outline(Wallet),
  'wallet.pass': outline(Wallet),
  gift: outline(Gift),
  'gift.fill': outline(Gift),
  crown: outline(Crown),
  'crown.fill': solid(Crown),
  trophy: outline(Trophy),
  'trophy.fill': solid(Trophy),

  // Nature and weather
  'sun.max': outline(Sun),
  'sun.max.fill': solid(Sun),
  moon: outline(Moon),
  'moon.fill': solid(Moon),
  'moon.stars': outline(Moon),
  cloud: outline(Cloud),
  'cloud.fill': solid(Cloud),
  'cloud.rain': outline(CloudRain),
  'cloud.snow': outline(CloudSnow),
  'cloud.sun': outline(CloudSun),
  'cloud.sun.fill': solid(CloudSun),
  'person.3': outline(Users),
  'person.3.fill': solid(Users),
  'chart.line.uptrend.xyaxis': outline(TrendingUp),
  'square.grid.2x2': outline(LayoutGrid),
  'circle.grid.3x3': outline(Grid3x3),
  'circle.grid.2x2': outline(CIRCLE_GRID_2X2),
  'circle.grid.2x2.fill': solid(CIRCLE_GRID_2X2),
  'app.dashed': outline(SquareDashed),
  'trash.slash': outline(TRASH_SLASH),
  'fork.knife': outline(Utensils),
  'text.bubble': outline(MessageSquareText),
  'text.bubble.fill': knockout(MessageSquareText),
  'plus.bubble': outline(MessageSquarePlus),
  'box.truck': outline(Truck),
  'list.bullet.below.rectangle': outline(LayoutList),
  'cloud.moon': outline(CloudMoon),
  'cloud.bolt': outline(CloudLightning),
  wind: outline(Wind),
  leaf: outline(Leaf),
  'leaf.fill': solid(Leaf),
  flame: outline(Flame),
  'flame.fill': solid(Flame),
  drop: outline(Droplet),
  'drop.fill': solid(Droplet),
  lightbulb: outline(Lightbulb),
  'lightbulb.fill': outline(Lightbulb),

  // Transport
  car: outline(Car),
  'car.fill': outline(Car),
  airplane: outline(Plane),
  bus: outline(Bus),
  'bus.fill': outline(Bus),
  bicycle: outline(Bike),

  // Reading
  book: outline(Book),
  'book.fill': solid(Book),
  'book.closed': outline(Book),
  'text.book.closed': outline(BookOpen),

  // Activity
  'rays': outline(LoaderCircle),
  // An indeterminate ProgressView's spinner (eight spokes; styles.css animates their fade).
  'progress.indicator': outline(ACTIVITY_INDICATOR),
  'circle.dotted': outline(LoaderCircle),

  // More common symbols: people, documents, arrows, text, shapes, money,
  // health, travel, weather, nature, media, devices, tools.
  'bed.double': outline(BedDouble),
  'bed.double.fill': solid(BedDouble),
  'qrcode.viewfinder': outline(ScanQrCode),
  barcode: outline(Barcode),
  'barcode.viewfinder': outline(ScanBarcode),
  viewfinder: outline(Scan),
  'questionmark.diamond': outline(BadgeQuestionMark),
  'rectangle.on.rectangle': outline(Copy),
  'rectangle.on.rectangle.angled': outline(GalleryVerticalEnd),
  'person.badge.plus': outline(UserPlus),
  'person.badge.minus': outline(UserMinus),
  'person.crop.circle.badge.plus': outline(UserPlus),
  'person.crop.circle.badge.xmark': outline(UserX),
  'person.crop.circle.badge.checkmark': outline(UserCheck),
  'person.crop.circle.fill.badge.checkmark': outline(UserCheck),
  'person.crop.square': outline(SquareUser),
  'person.crop.square.fill': knockout(SquareUser),
  'person.2.fill': solid(Users),
  'person.2.circle': outline(Users),
  'person.text.rectangle': outline(IdCard),
  'person.crop.rectangle': outline(Contact),
  'folder.badge.plus': outline(FolderPlus),
  'folder.badge.minus': outline(FolderMinus),
  'doc.badge.plus': outline(FilePlus),
  'doc.on.doc.fill': outline(Copy),
  'calendar.badge.plus': outline(CalendarPlus),
  'calendar.badge.clock': outline(CalendarClock),
  'calendar.badge.exclamationmark': outline(CalendarX),
  'clock.arrow.circlepath': outline(History),
  'clock.badge.checkmark': outline(ClockCheck),
  'arrow.uturn.backward': outline(Undo2),
  'arrow.uturn.forward': outline(Redo2),
  'arrow.uturn.left': outline(Undo2),
  'arrow.uturn.right': outline(Redo2),
  'arrow.up.left.and.arrow.down.right': outline(Maximize2),
  'arrow.down.right.and.arrow.up.left': outline(Minimize2),
  'arrow.up.right.square': outline(SquareArrowOutUpRight),
  'arrow.up.forward.app': outline(SquareArrowOutUpRight),
  'arrow.right.square': outline(SquareArrowRight),
  'arrow.left.square': outline(SquareArrowLeft),
  'arrow.up.square': outline(SquareArrowUp),
  'arrow.down.square': outline(SquareArrowDown),
  'arrow.triangle.branch': outline(GitBranch),
  'arrow.triangle.merge': outline(GitMerge),
  'arrow.triangle.swap': outline(ArrowLeftRight),
  'arrow.up.and.down': outline(ArrowUpDown),
  'arrow.left.and.right': outline(ArrowLeftRight),
  'arrow.down.left': outline(ArrowDownLeft),
  'arrow.down.right': outline(ArrowDownRight),
  'arrow.up.left': outline(ArrowUpLeft),
  'arrowshape.turn.up.left': outline(Reply),
  'arrowshape.turn.up.right': outline(Forward),
  'arrowshape.turn.up.left.2': outline(ReplyAll),
  'arrowshape.left': outline(ArrowBigLeft),
  'arrowshape.right': outline(ArrowBigRight),
  'arrowshape.up': outline(ArrowBigUp),
  'arrowshape.down': outline(ArrowBigDown),
  'square.on.square': outline(Copy),
  'square.on.circle': outline(Shapes),
  'square.on.circle.fill': solid(SQUARE_ON_CIRCLE_FILL),
  'plus.square': outline(SquarePlus),
  'plus.square.fill': knockout(SquarePlus),
  'minus.square': outline(SquareMinus),
  'minus.square.fill': knockout(SquareMinus),
  'xmark.square': outline(SquareX),
  'xmark.square.fill': knockout(SquareX),
  'plus.app': outline(SquarePlus),
  'star.circle': outline(Star),
  'star.leadinghalf.filled': outline(StarHalf),
  'star.slash': outline(StarOff),
  'heart.slash': outline(HeartOff),
  'heart.circle': outline(Heart),
  'bell.slash': outline(BellOff),
  'bell.badge': outline(BellDot),
  'bell.badge.fill': outline(BellDot),
  'envelope.badge': outline(MailWarning),
  'envelope.open.fill': outline(MailOpen),
  'tray.full': outline(Inbox),
  'tray.fill': solid(TRAY_FILL),
  'tray.and.arrow.down': outline(Inbox),
  'tray.and.arrow.up': outline(Inbox),
  'phone.arrow.up.right': outline(PhoneOutgoing),
  'phone.arrow.down.left': outline(PhoneIncoming),
  'phone.down': outline(PhoneOff),
  video: outline(Video),
  'video.fill': solid(Video),
  'video.slash': outline(VideoOff),
  'bubble.left.and.bubble.right': outline(MessagesSquare),
  'bubble.left.and.bubble.right.fill': outline(MessagesSquare),
  'bubble.right': outline(MessageSquare),
  'bubble.left.fill': solid(MessageSquare),
  'quote.bubble': outline(MessageSquareQuote),
  'ellipsis.bubble': outline(MessageSquareMore),
  'exclamationmark.bubble': outline(MessageSquareWarning),
  'text.alignleft': outline(TextAlignStart),
  'text.aligncenter': outline(TextAlignCenter),
  'text.alignright': outline(TextAlignEnd),
  'text.justify': outline(TextAlignJustify),
  bold: outline(Bold),
  italic: outline(Italic),
  underline: outline(Underline),
  strikethrough: outline(Strikethrough),
  'textformat.size': outline(ALargeSmall),
  character: outline(Type),
  'list.dash': outline(List),
  'list.bullet.indent': outline(ListIndentIncrease),
  'increase.indent': outline(ListIndentIncrease),
  'decrease.indent': outline(ListIndentDecrease),
  'list.clipboard': outline(ClipboardList),
  'square.grid.3x3': outline(Grid3x3),
  'square.grid.2x2.fill': solid(LayoutGrid),
  'rectangle.grid.1x2': outline(Rows2),
  'rectangle.split.2x1': outline(Columns2),
  'sidebar.left': outline(PanelLeft),
  'sidebar.right': outline(PanelRight),
  'rectangle.stack': outline(Layers),
  'square.stack': outline(Layers),
  'square.stack.3d.up': outline(Layers3),
  rectangle: outline(RectangleHorizontal),
  'rectangle.fill': solid(RectangleHorizontal),
  'rectangle.portrait': outline(RectangleVertical),
  triangle: outline(Triangle),
  'triangle.fill': solid(Triangle),
  diamond: outline(Diamond),
  'diamond.fill': solid(Diamond),
  hexagon: outline(Hexagon),
  'hexagon.fill': solid(Hexagon),
  octagon: outline(Octagon),
  'checkmark.seal': outline(BadgeCheck),
  'checkmark.seal.fill': knockout(BadgeCheck),
  'xmark.octagon': outline(OctagonX),
  'xmark.octagon.fill': outline(OctagonX),
  'exclamationmark.octagon': outline(OctagonAlert),
  nosign: outline(Ban),
  'slash.circle': outline(Ban),
  number: outline(Hash),
  percent: outline(Percent),
  dollarsign: outline(DollarSign),
  'dollarsign.circle': outline(CircleDollarSign),
  'dollarsign.circle.fill': knockout(CircleDollarSign),
  eurosign: outline(Euro),
  sterlingsign: outline(PoundSterling),
  yensign: outline(JapaneseYen),
  bitcoinsign: outline(Bitcoin),
  banknote: outline(Banknote),
  basket: outline(ShoppingBasket),
  storefront: outline(Store),
  'building.columns': outline(Landmark),
  ticket: outline(Ticket),
  'chart.bar.fill': outline(ChartBar),
  'chart.bar.xaxis': outline(ChartColumn),
  'chart.line.downtrend.xyaxis': outline(TrendingDown),
  waveform: outline(AudioWaveform),
  'waveform.path.ecg': outline(Activity),
  cross: outline(Cross),
  'cross.case': outline(BriefcaseMedical),
  pills: outline(Pill),
  bandage: outline(Bandage),
  stethoscope: outline(Stethoscope),
  brain: outline(Brain),
  'brain.head.profile': outline(Brain),
  'figure.stand': outline(PersonStanding),
  dumbbell: outline(Dumbbell),
  tram: outline(TramFront),
  'train.side.front.car': outline(TrainFront),
  ferry: outline(Ship),
  sailboat: outline(Sailboat),
  fuelpump: outline(Fuel),
  parkingsign: outline(SquareParking),
  tent: outline(Tent),
  'mountain.2': outline(Mountain),
  tree: outline(TreeDeciduous),
  thermometer: outline(Thermometer),
  'thermometer.medium': outline(Thermometer),
  umbrella: outline(Umbrella),
  snowflake: outline(Snowflake),
  'sun.min': outline(SunDim),
  sunrise: outline(Sunrise),
  sunset: outline(Sunset),
  'cloud.drizzle': outline(CloudDrizzle),
  'cloud.fog': outline(CloudFog),
  'cloud.heavyrain': outline(CloudRainWind),
  'cloud.hail': outline(CloudHail),
  tornado: outline(Tornado),
  humidity: outline(Droplets),
  pawprint: outline(PawPrint),
  hare: outline(Rabbit),
  tortoise: outline(Turtle),
  fish: outline(Fish),
  bird: outline(Bird),
  // The Swift logo: a bird in flight.
  swift: outline(Bird),
  'apple.logo': solid(APPLE_LOGO),
  applelogo: solid(APPLE_LOGO),
  cat: outline(Cat),
  dog: outline(Dog),
  carrot: outline(Carrot),
  'cup.and.saucer': outline(Coffee),
  mug: outline(Coffee),
  wineglass: outline(Wine),
  'birthday.cake': outline(Cake),
  film: outline(Film),
  'film.stack': outline(Clapperboard),
  'camera.rotate': outline(SwitchCamera),
  'photo.stack': outline(Images),
  'photo.on.rectangle.angled': outline(Images),
  'music.note.list': outline(ListMusic),
  'music.mic': outline(MicVocal),
  radio: outline(Radio),
  airplayaudio: outline(Airplay),
  airplayvideo: outline(Airplay),
  'play.rectangle': outline(MonitorPlay),
  'play.square': outline(SquarePlay),
  icloud: outline(Cloud),
  'icloud.fill': solid(Cloud),
  'icloud.and.arrow.up': outline(CloudUpload),
  'icloud.and.arrow.down': outline(CloudDownload),
  externaldrive: outline(HardDrive),
  'server.rack': outline(Server),
  cylinder: outline(Database),
  network: outline(Network),
  faceid: outline(ScanFace),
  touchid: outline(Fingerprint),
  'lock.shield': outline(ShieldCheck),
  'hand.tap': outline(Pointer),
  'hand.wave': outline(Hand),
  'hand.thumbsdown': outline(ThumbsDown),
  'hand.thumbsdown.fill': solid(ThumbsDown),
  eyeglasses: outline(Glasses),
  sparkle: outline(Sparkle),
  'wand.and.stars': outline(WandSparkles),
  paintbrush: outline(Paintbrush),
  paintpalette: outline(Palette),
  'pencil.tip': outline(PenTool),
  'pencil.tip.crop.circle': outline(PenTool),
  'pencil.tip.crop.circle.fill': knockout(PenTool),
  scribble: outline(LineSquiggle),
  'scribble.variable': outline(LineSquiggle),
  'character.textbox': outline(TextCursorInput),
  'pencil.slash': outline(PencilOff),
  highlighter: outline(Highlighter),
  scissors: outline(Scissors),
  ruler: outline(Ruler),
  eyedropper: outline(Pipette),
  lasso: outline(Lasso),
  crop: outline(Crop),
  gauge: outline(Gauge),
  speedometer: outline(Gauge),
  hammer: outline(Hammer),
  puzzlepiece: outline(Puzzle),
  dice: outline(Dices),
  newspaper: outline(Newspaper),
  'books.vertical': outline(Library),
  'books.vertical.fill': solid(BOOKS_VERTICAL_FILL),
  'note.text': outline(StickyNote),
  note: outline(StickyNote),
  'location.circle': outline(LocateFixed),
  'location.north': outline(Navigation2),
  'location.slash': outline(MapPinOff),
  'signpost.right': outline(Signpost),
  'globe.americas': outline(Earth),
  'globe.europe.africa': outline(Earth),
  'bolt.slash': outline(ZapOff),
  'battery.25': outline(BatteryLow),
  'battery.50': outline(BatteryMedium),
  'battery.75': outline(BatteryFull),
  'battery.0': outline(BatteryWarning),
  'wifi.slash': outline(WifiOff),
  cellularbars: outline(Signal),
  infinity: outline(Infinity),
  sum: outline(Sigma),
  command: outline(Command),
  option: outline(Option),
  'delete.left': outline(Delete),
  return: outline(CornerDownLeft),
  'xmark.bin': outline(Trash2),
  'square.and.arrow.up.on.square': outline(Share2),
  'link.badge.plus': outline(Link2),
  'shippingbox.circle': outline(Package),
  'graduationcap.fill': solid(GraduationCap),
  medal: outline(Medal),
  rosette: outline(Award),
  'gift.circle': outline(Gift),
  studentdesk: outline(School),
};

/** Number of SF Symbol names the map lists (more resolve as variants, see `lookupSymbol`). */
export const SF_SYMBOL_COUNT = Object.keys(SF_SYMBOLS).length;

export function lookupSymbol(systemName: string): SymbolGlyph | undefined {
  const exact = SF_SYMBOLS[systemName];
  if (exact) return exact;
  if (!(systemName in variants)) variants[systemName] = resolveVariant(systemName) ?? null;
  return variants[systemName] ?? undefined;
}

/** Resolved variants (null: none); an object because lucide's `Map` icon shadows the global. */
const variants: Record<string, SymbolGlyph | null> = Object.create(null);

/** Shapes that alone would stand in for too much (`circle.grid.2x2` is not a circle). */
const GENERIC_SHAPES = new Set(['circle', 'square', 'rectangle', 'app', 'capsule', 'triangle', 'diamond', 'hexagon', 'octagon', 'seal', 'shield']);

/**
 * A name the map does not list is drawn as the closest listed one: SF Symbol
 * names extend a base with modifiers, so `person.crop.circle.badge.xmark`
 * is drawn as `person.crop.circle`, `trash.circle.fill` as `trash.fill` and
 * `speaker.wave.2.fill` as `speaker.wave.2`. A `.badge…` suffix is dropped,
 * then trailing parts one at a time; a name with `.fill` tries every filled
 * form first. A `.slash` variant keeps its slash (`speaker.slash.fill` is
 * drawn as `speaker.slash`) and is never drawn as its base, which would
 * mean the opposite (`bell.slash` is "notifications off"); nor is a name
 * drawn as a bare shape. Unresolved names get the caller's placeholder.
 */
function resolveVariant(systemName: string): SymbolGlyph | undefined {
  let parts = systemName.split('.');
  const fill = parts.includes('fill');
  parts = parts.filter((part) => part !== 'fill');
  const badge = parts.indexOf('badge');
  if (badge > 0) parts = parts.slice(0, badge);
  // `trash.circle`, `arrow.down.circle.fill`, `heart.square.fill`: the base
  // glyph inside the enclosure (drawn whole, or filled with the glyph
  // knocked out), unless the table lists the full name's base form.
  const enclosure = parts.at(-1);
  if ((enclosure === 'circle' || enclosure === 'square') && parts.length > 1) {
    const listed = SF_SYMBOLS[parts.join('.')];
    if (listed) {
      // The listed outline form, its enclosure filled (`arrow.down.circle.fill`).
      if (fill && enclosingShapeIndex(listed.icon) >= 0) return { icon: listed.icon, fill: 'knockout' };
    } else {
      const base = parts.slice(0, -1).join('.');
      const glyph = GENERIC_SHAPES.has(base) ? undefined : lookupSymbol(base);
      // A base whose filled form is a silhouette (`moon.fill`) is knocked
      // out solid, as `moon.circle.fill` draws its moon.
      if (glyph) return enclosed(glyph.icon, enclosure, fill, fill && lookupSymbol(`${base}.fill`)?.fill === 'solid');
    }
  }
  const bases: string[] = [];
  // Never shorter than the slash.
  const shortest = Math.max(1, parts.indexOf('slash') + 1);
  for (let length = parts.length; length >= shortest; length--) {
    const base = parts.slice(0, length).join('.');
    if (length === 1 && parts.length > 1 && GENERIC_SHAPES.has(base)) break;
    bases.push(base);
  }
  // A filled form at any length before an outline: `heart.circle.fill` is
  // closer to `heart.fill` than to `heart.circle`.
  const candidates = fill ? [...bases.map((base) => `${base}.fill`), ...bases] : bases;
  for (const name of candidates) {
    const glyph = SF_SYMBOLS[name];
    if (glyph) return glyph;
  }
  return undefined;
}

/**
 * `icon` at 55% inside a circle or rounded square: outlined (`X.circle`), or
 * filled with the glyph knocked out of it (`X.circle.fill`), as SF Symbols
 * draw their enclosed forms; `solid`, the glyph's shapes are knocked out
 * whole rather than as strokes.
 */
function enclosed(icon: IconNode, enclosure: 'circle' | 'square', fill: boolean, solid = false): SymbolGlyph {
  const outer: IconNode[number] =
    enclosure === 'circle' ? ['circle', { cx: '12', cy: '12', r: '10' }] : ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '4' }];
  // Scaled about the center; the stroke is widened to keep its weight.
  const inner = icon.map(
    ([tag, attrs]) =>
      [tag, { ...attrs, transform: 'translate(5.4 5.4) scale(0.55)', 'stroke-width': '3.3', ...(solid ? { [SOLID_MARK]: '' } : {}) }] as IconNode[number],
  );
  return { icon: [outer, ...inner], fill: fill ? 'knockout' : 'outline' };
}

/**
 * The name of the filled form of `systemName` when the table can draw one
 * (`house` → `house.fill`), else the name itself: a tab bar shows its items'
 * symbols filled, as iOS does (`symbolVariants` `.fill`).
 */
export function filledSymbolName(systemName: string): string {
  if (systemName.split('.').includes('fill')) return systemName;
  const filled = `${systemName}.fill`;
  const glyph = lookupSymbol(filled);
  return glyph && glyph.fill !== 'outline' ? filled : systemName;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * The shape a `knockout` glyph fills: the enclosing circle or rectangle
 * (lucide lists it first in most glyphs but not all: `CirclePlay` starts
 * with its triangle), else the first shape.
 */
export function outerShapeIndex(icon: IconNode): number {
  const index = enclosingShapeIndex(icon);
  return index >= 0 ? index : 0;
}

/** The index of the circle or rectangle enclosing `icon`'s other marks, or -1. */
function enclosingShapeIndex(icon: IconNode): number {
  return icon.findIndex(([tag, attrs]) => {
    const n = (key: string): number => Number(attrs[key] ?? 0);
    return (tag === 'circle' && n('r') >= 9) || (tag === 'rect' && n('width') >= 16 && n('height') >= 12);
  });
}


/**
 * Build the `<svg>` for a symbol. Returns `null` for unknown names; the
 * caller draws a placeholder in that case.
 */
export function renderSymbol(systemName: string): SVGElement | null {
  const glyph = lookupSymbol(systemName);
  if (!glyph) return null;
  const svg = createElement(glyph.icon, {
    width: '1em',
    height: '1em',
    'aria-hidden': 'true',
    focusable: 'false',
  });
  if (svg.namespaceURI !== SVG_NS) return null;
  svg.setAttribute('data-sb-symbol', systemName);
  svg.setAttribute('data-sb-glyph-fill', glyph.fill);
  if (glyph.fill === 'solid') {
    for (const shape of Array.from(svg.children)) {
      shape.setAttribute('fill', 'currentColor');
    }
  } else if (glyph.fill === 'knockout') {
    const shapes = Array.from(svg.children);
    const outer = outerShapeIndex(glyph.icon);
    shapes.forEach((shape, index) => {
      if (index === outer) {
        shape.setAttribute('fill', 'currentColor');
      } else {
        shape.setAttribute('stroke', KNOCKOUT_COLOR);
        shape.setAttribute('fill', shape.hasAttribute(SOLID_MARK) ? KNOCKOUT_COLOR : 'none');
        shape.removeAttribute(SOLID_MARK);
      }
    });
  }
  return svg;
}
